import { z } from 'zod';

import type { ArScheduleExpectedCash } from './ar-collection.js';
import type { ArInvoiceListedIssue } from './ar-invoice-issues.js';
import type {
  BillingGroupResponse,
  CustomerBalanceSummaryResponse,
  CustomerEmailResponse,
  ResolvedRevenueCycle,
} from './billing.js';
import type {
  CustomBreakdown,
  CustomBreakdownInput,
  DimensionSplit,
  DimensionSplitInput,
  SegmentSplit,
  SegmentSplitInput,
} from './classification.js';
import type {
  AddressType,
  ApAllocationType,
  ApClassificationMode,
  ArBoletoStatus,
  ArClassificationSource,
  ArDebitNoteStatus,
  ArInvoiceStatus,
  ArNfseCancelAction,
  ArNfseCancellationStatus,
  ArNfseCancelType,
  ArNfseEmissionMode,
  ArNfseEmissionStatus,
  ArWithholdingTaxType,
  BillingServiceNfIssuer,
  ClientReimbursementStatus,
  FiscalNoteDocumentType,
  PartyType,
  TaxIdType,
} from './generated/enums.js';
import type { PartyResponse } from './party.js';

// Accounts Receivable (AR) canonical types
// These interfaces are used by importers, services and APIs to represent
// normalized AR data independent of source systems.

export type ArInvoiceStatusCanonical =
  | 'DRAFT'
  | 'AWAITING_NF'
  | 'BLOCKED'
  // A reviewed pre-billing draft whose source data changed after generation (ALC-1201).
  // Internal, engine-driven — never a valid import/normalization target.
  | 'STALE'
  | 'ISSUED'
  | 'PARTIALLY_PAID'
  // Economically paid per an external source system (a payment gateway reported a
  // captured charge via the External API) but NOT yet confirmed by a bank movement.
  // There is no ArReceipt behind it, so the GL receivable is still outstanding and it
  // is still an OPEN receivable. Bank reconciliation later matches the credit and
  // promotes it to PAID.
  | 'PAID_UNCONFIRMED'
  // Some of the captured charge's payment schedule has been settled in cash and some has
  // not (ALC-2824) — the card-installment shape: one invoice, one card transaction, the
  // acquirer remitting parcel by parcel. Still an OPEN receivable, like PAID_UNCONFIRMED.
  // NOT the same as PARTIALLY_PAID, where the CUSTOMER still owes the balance.
  | 'PARTIALLY_RECEIVED'
  | 'PAID'
  | 'VOID'
  // An invoice migrated from the customer's previous system during onboarding. Informational
  // only: never posts to GL, never emits NF, never a reconciliation candidate, never forecast.
  // Written only at creation (CSV `asMigrated`, historical backfill); no transition in or out.
  | 'MIGRATED';

/**
 * Canonical AR invoice status progression, used for ordering invoice lists.
 *
 * This array MUST stay identical to the declaration order of the `ar_invoice_status`
 * Postgres enum (see `packages/db/prisma/migrations/*` — DRAFT/ISSUED/PARTIALLY_PAID/PAID/VOID
 * at creation, then AWAITING_NF/BLOCKED after DRAFT, STALE before ISSUED, PAID_UNCONFIRMED
 * after PARTIALLY_PAID, then PARTIALLY_RECEIVED after PAID_UNCONFIRMED). Postgres orders enum
 * columns by declaration order, so the API can
 * satisfy a `sortBy=status` request with a plain `ORDER BY invoice_status` and the web client
 * can rank statuses locally from this same array without the two drifting apart.
 *
 * Do not reorder it to express a nicer product progression: that would silently desynchronise
 * client ranking from server ordering. Changing the order requires recreating the DB enum.
 *
 * ALC-2824 inserted PARTIALLY_RECEIVED at rank 7, pushing PAID to 8 and VOID to 9. That
 * matches `ALTER TYPE "ar_invoice_status" ADD VALUE 'PARTIALLY_RECEIVED' AFTER
 * 'PAID_UNCONFIRMED'` in
 * `20260826140000_alc2824_ar_invoice_status_partially_received/migration.sql`, whose comment
 * ratifies the same delta from the database side. The renumbering below is NOT a reordering
 * of existing statuses — every pre-existing pair keeps its relative order, exactly as the
 * Postgres type does; only the absolute indices shift to make room. These two must be
 * changed together or `sortBy=status` silently disagrees between server and client.
 *
 * MIGRATED ranks LAST (10), mirroring `ALTER TYPE "ar_invoice_status" ADD VALUE 'MIGRATED'
 * AFTER 'VOID'` in `20260918120100_ar_invoice_status_migrated/migration.sql`. A migrated
 * invoice is outside the live lifecycle, so it sorts after the terminal states. If the enum
 * order ever diverges from this map, Prisma `orderBy` and raw SQL `ORDER BY invoice_status`
 * disagree.
 */
const AR_INVOICE_STATUS_RANK: Record<ArInvoiceStatus, number> = {
  DRAFT: 0,
  AWAITING_NF: 1,
  BLOCKED: 2,
  STALE: 3,
  ISSUED: 4,
  PARTIALLY_PAID: 5,
  PAID_UNCONFIRMED: 6,
  PARTIALLY_RECEIVED: 7,
  PAID: 8,
  VOID: 9,
  MIGRATED: 10,
};

/**
 * Statuses hidden from every AR invoice list and summary when the caller sends NO status
 * filter. Today that is only MIGRATED: an invoice migrated from the customer's previous
 * system is informational and must not crowd day-to-day listings. A caller that asks for
 * it explicitly (`status=MIGRATED` or a `statusIn` containing it) still gets it — the web
 * "Todas" tab does exactly that.
 *
 * Consumers: `ArInvoiceService.buildArInvoiceWhereClause` and `findSummary` (API),
 * `ArQueryCore.invoiceWhere` / `invoiceSummaryWhereSql` (MCP), and the web
 * `ACTIVE_INVOICE_STATUSES` twin in `ArInvoicesPage.tsx`.
 */
export const AR_DEFAULT_LIST_HIDDEN_STATUSES: readonly ArInvoiceStatus[] = ['MIGRATED'];

/** True for an invoice migrated from a previous system (informational, read-only in v1). */
export function isMigratedArInvoice(status: string | null | undefined): boolean {
  return status === 'MIGRATED';
}

export const AR_INVOICE_STATUS_SORT_ORDER: readonly ArInvoiceStatus[] = (
  Object.keys(AR_INVOICE_STATUS_RANK) as ArInvoiceStatus[]
).sort((left, right) => AR_INVOICE_STATUS_RANK[left] - AR_INVOICE_STATUS_RANK[right]);

/**
 * Rank of `status` in the canonical progression. `Record<ArInvoiceStatus, number>` above is
 * compile-forced, so a new enum member cannot be added without being given a rank here.
 */
export function arInvoiceStatusRank(status: ArInvoiceStatus): number {
  return AR_INVOICE_STATUS_RANK[status] ?? AR_INVOICE_STATUS_SORT_ORDER.length;
}

export type ArReceiptStatusCanonical =
  'DRAFT' | 'POSTED' | 'PARTIALLY_ALLOCATED' | 'FULLY_ALLOCATED' | 'VOID';

export interface ArCustomerCanonical {
  /** External customer identifier in the source system */
  externalCustomerId: string;
  /** External identifier or code for the legal entity */
  entityExternalId: string;

  name: string;
  taxId?: string;
  email?: string;

  paymentTerms?: string;
  creditLimit?: number;
  isBlocked?: boolean;
  blockReason?: string;
}

export interface ArInvoiceLineCanonical {
  externalLineId?: string;
  lineNumber?: number;

  description: string;

  quantity?: number;
  unitPrice?: number;
  /** Line amount in invoice currency */
  lineAmount: number;

  /** Optional GL mapping hint; not a journal line */
  revenueAccountCode?: string;
  /** BillingService identifier when the invoice line was linked to the service catalog. */
  serviceId?: string | null;
}

export interface ArInstallmentCanonical {
  installmentNumber: number;
  /** Due date in ISO YYYY-MM-DD */
  dueDate: string;
  amount: number;
}

/**
 * A tax withheld at source by the customer on AR revenue (retido na fonte).
 * The amount is debited to the mapped RECOVERABLE GL slot at invoice posting,
 * and the receivable is netted by the sum of withholdings.
 */
export interface ArInvoiceWithholdingCanonical {
  taxType: ArWithholdingTaxType;
  amount: number;
}

export interface ArInvoiceCanonical {
  /** External invoice identifier in the source system */
  externalInvoiceId: string;
  /** External identifier or code for the legal entity */
  entityExternalId: string;
  /** External customer identifier, must match ArCustomerCanonical.externalCustomerId */
  customerExternalId: string;
  /**
   * Customer tax id (CNPJ/CPF). When provided and `customerExternalId` does not
   * resolve, the importer resolves or creates the Client by this tax id.
   */
  customerTaxId?: string;
  /** Customer legal name, used when auto-creating a Client during import. */
  customerName?: string;
  /**
   * Customer e-mail. With no code and no tax id (a card or gateway buyer with no CPF), the
   * importer resolves or creates the Client by this e-mail, named `customerName`.
   */
  customerEmail?: string;

  invoiceNumber?: string;
  /**
   * External integration idempotency key (Developer API `externalReferenceId`,
   * `GET /v1/invoices/by-reference/:externalReferenceId`). Not a bank reconciliation
   * token — CSV import must not populate this from collection/memo tags.
   */
  externalRef?: string;

  /**
   * Service catalog code or external product id (e.g. the source "Prod" code). When set
   * and no explicit revenue account is provided, the importer resolves fiscal and revenue
   * metadata from the matching BillingService.
   */
  serviceCode?: string;

  /**
   * The billing contract this charge is booked against, and the contract cycle it bills
   * (ALC-2821). Both are written into the invoice INSERT rather than stamped afterwards,
   * which is the whole reason they are on this shape.
   *
   * The distinction from `metadata` and `externalPaymentRefId` — which are deliberately NOT
   * here — is that these two are ordinary AR columns that internal contract billing already
   * writes, not External-API-only concepts. And their timing is load-bearing rather than
   * cosmetic: an invoice that exists for even a moment without its cycle marker reads as an
   * ordinary one-off charge, and issuing it recognizes revenue that should have been deferred
   * over a whole service period — a mistake no retry can undo, because the journal is posted.
   * Written with the row, the invoice can never be in that state.
   *
   * CSV import leaves both undefined, exactly as it leaves `externalRef`.
   */
  contractId?: string;
  /** Inclusive start of the contract cycle this charge bills (ISO YYYY-MM-DD). */
  contractBillingPeriodStart?: string;

  /** Taxes withheld at source by the customer (retido na fonte). */
  withholdings?: ArInvoiceWithholdingCanonical[];

  /**
   * The NF the customer's own system issued for this invoice's EXTERNAL-issuer services
   * (ALC-5091): its number and its issue date (ISO YYYY-MM-DD), both or neither. With them, the
   * invoice's ISS is booked as owed at approval; without them, an invoice whose services all
   * issue the NF outside Alchemy ERP parks its ISS until the NF is linked.
   */
  externalFiscalNoteNumber?: string;
  externalFiscalNoteIssueDate?: string;

  /** Issue date in ISO YYYY-MM-DD */
  issueDate: string;
  /** Optional header-level due date in ISO YYYY-MM-DD */
  dueDate?: string;
  /** Optional competency date in ISO YYYY-MM-DD */
  competencyDate?: string;
  /** Optional managerial date in ISO YYYY-MM-DD */
  managerialDate?: string;

  currency: string;
  exchangeRate?: number;

  subtotal?: number;
  /** Own ISS to provision (not withheld by the customer). */
  issProprio?: number;
  taxTotal?: number;
  /** Total invoice amount in invoice currency */
  total: number;
  /** Net collectible amount (valor líquido). Optional when service-driven taxes can calculate it. */
  net?: number;

  status?: ArInvoiceStatusCanonical;

  description?: string;
  memo?: string;

  paymentTerms?: string;

  lines: ArInvoiceLineCanonical[];
  /** Optional explicit installments; if omitted, engine may derive a single installment = total */
  installments?: ArInstallmentCanonical[];
}

/**
 * One pre-billing invoice created from the same fields as a CSV import row
 * (`AR_INVOICE_IMPORT_SPEC`). Contract linking is intentionally absent.
 */
export interface CreateArPreBillingInvoiceRequest {
  /** Entity CNPJ. Omitted only when the tenant has a single entity. */
  entityName?: string;
  invoiceNumber?: string;
  /** Issue date. ISO `YYYY-MM-DD` or the same `dd/mm/yyyy` shapes the CSV parser accepts. */
  issueDate: string;
  dueDate?: string;
  competencyDate?: string;
  managerialDate?: string;
  customerTaxId?: string;
  customerName?: string;
  customerCode?: string;
  /**
   * Invoice currency. When omitted, pre-billing uses the entity book's functional
   * currency. It is never assumed to be BRL.
   */
  currency?: string;
  serviceCode: string;
  description?: string;
  grossAmount: number;
  netAmount?: number;
  irrf?: number;
  inss?: number;
  pisRetido?: number;
  cofinsRetido?: number;
  csllRetido?: number;
  issRetido?: number;
  issProprio?: number;
}

export interface CreateArPreBillingInvoiceResponse {
  invoiceId: string;
  outcome: 'CREATED' | 'UPDATED';
  warnings: { warning: string; code?: string }[];
}

export interface ArReceiptAllocationCanonical {
  /** External invoice identifier to allocate against */
  invoiceExternalId: string;
  /** Optional installment number inside the invoice */
  installmentNumber?: number;
  amount: number;
}

export interface ArReceiptCanonical {
  /** External receipt identifier in the source system */
  externalReceiptId: string;
  /** External identifier or code for the legal entity */
  entityExternalId: string;
  /** External customer identifier */
  customerExternalId: string;

  receiptNumber?: string;
  externalRef?: string;

  /** Receipt date in ISO YYYY-MM-DD */
  receiptDate: string;

  amount: number;
  currency: string;

  status?: ArReceiptStatusCanonical;

  allocations: ArReceiptAllocationCanonical[];
}

export interface ArDebitNoteLineResponse {
  lineId: string;
  debitNoteId: string;
  billId: string;
  amount: number;
  description?: string | null;
  billStatus?: string | null;
  paymentDate?: string | null;
  paymentDates?: string[] | null;
  billUrl?: string | null;
}

export interface ArDebitNoteAllocationResponse {
  allocationId: string;
  debitNoteId: string;
  receiptId: string;
  entityId: string;
  amount: number;
  allocationDate?: string | null;
}

export interface ArDebitNoteResponse {
  debitNoteId: string;
  entityId: string;
  customerId: string;
  customerName?: string | null;
  customerTaxId?: string | null;
  number?: string | null;
  competencyPeriod?: string | null;
  dueDate?: string | null;
  totalAmount: number;
  allocatedAmount: number;
  currencyCode: string;
  status: ArDebitNoteStatus;
  paymentBankAccountId?: string | null;
  lineCount?: number;
  issuedAt?: string | null;
  issuedByUserId?: string | null;
  notes?: string | null;
  lines?: ArDebitNoteLineResponse[];
  allocations?: ArDebitNoteAllocationResponse[];
  createdAt: string;
  updatedAt?: string | null;
}

export type ArDebitNoteReimbursementBillStatus =
  'AWAITING_PAYMENT' | 'READY_FOR_REIMBURSEMENT' | 'BILLED' | 'REIMBURSED';

export interface ArDebitNoteReimbursementStatusSummary {
  count: number;
  amount: number;
}

export interface ArDebitNoteReimbursementBillResponse {
  billId: string;
  entityId: string;
  customerId: string;
  customerTaxId?: string | null;
  vendorId?: string | null;
  vendorName?: string | null;
  description?: string | null;
  costCenterId?: string | null;
  costCenterName?: string | null;
  customFields?: Record<string, unknown> | null;
  currencyCode: string;
  billStatus?: string | null;
  billCompetencyDate?: string | null;
  clientReimbursementStatus?: ClientReimbursementStatus | null;
  reimbursementStatus: ArDebitNoteReimbursementBillStatus;
  totalAmount: number;
  futureAmount: number;
  readyAmount: number;
  pendingAmount: number;
  reimbursedAmount: number;
  debitNoteId?: string | null;
  debitNoteNumber?: string | null;
  hasOriginalDocument: boolean;
  billUrl: string;
}

export interface ArDebitNoteReimbursementGroupResponse {
  customerId: string;
  customerName?: string | null;
  customerTaxId?: string | null;
  entityId: string;
  currencyCode: string;
  billCount: number;
  futureAmount: number;
  readyAmount: number;
  pendingAmount: number;
  reimbursedAmount: number;
  statuses: Record<ArDebitNoteReimbursementBillStatus, ArDebitNoteReimbursementStatusSummary>;
}

export interface ArDebitNoteCustomerReimbursementsResponse extends ArDebitNoteReimbursementGroupResponse {
  bills: Record<ArDebitNoteReimbursementBillStatus, ArDebitNoteReimbursementBillResponse[]>;
  debitNotes: ArDebitNoteResponse[];
}

export interface CreateArDebitNoteRequest {
  entityId: string;
  customerId: string;
  competencyPeriod?: string | null;
  dueDate?: string | null;
  billIds: string[];
  notes?: string | null;
  issueImmediately?: boolean;
  paymentBankAccountId?: string | null;
}

export interface ListArDebitNotesRequest {
  status?: string;
  customerId?: string;
  entityId?: string;
  competencyPeriod?: string;
  amountMin?: number;
  amountMax?: number;
  createdFrom?: string;
  createdTo?: string;
  dueFrom?: string;
  dueTo?: string;
}

export interface ListEligibleDebitNoteBillsRequest {
  entityId?: string;
  customerId?: string;
  competencyDateFrom?: string;
  competencyDateTo?: string;
  paymentDateFrom?: string;
  paymentDateTo?: string;
  amountMin?: number;
  amountMax?: number;
  reimbursementStatus?: ArDebitNoteReimbursementBillStatus;
}

export interface EligibleDebitNoteBillResponse extends ArDebitNoteReimbursementBillResponse {
  clientReimbursementStatus: 'OPEN';
  reimbursementStatus: 'READY_FOR_REIMBURSEMENT';
}

export interface AllocateArDebitNoteReceiptRequest {
  receiptId: string;
  amount: number;
  allocationDate?: string | null;
}

export interface FindEmittedNfsQuery {
  entityId?: string;
  currency?: string;
  documentTypes?: FiscalNoteDocumentType[];
  statuses?: string[];
  customerIds?: string[];
  issueDateFrom?: string;
  issueDateTo?: string;
  totalAmount?: number;
  totalAmountOp?: 'eq' | 'gte' | 'lte';
  sortBy?: 'issueDate' | 'documentNumber' | 'documentType' | 'status' | 'totalAmount';
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface EmittedNfListItemResponse {
  fiscalNoteId: string;
  entityId: string;
  entityName: string;
  accessKey: string;
  documentNumber?: string | null;
  rpsNumber?: string | null;
  documentType: FiscalNoteDocumentType;
  status?: string | null;
  hasPdf?: boolean;
  totalAmount?: number | null;
  currency?: string | null;
  linkedInvoiceId?: string | null;
  linkedInvoiceStatus?: ArInvoiceStatus | null;
  customerId?: string | null;
  customerName?: string | null;
  linkedInvoiceNumber?: string | null;
  issueDate?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface EmittedNfListResponse {
  data: EmittedNfListItemResponse[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Alphanumeric CNPJ format: 12 alphanumeric positions plus 2 numeric check digits, uppercase.
 * A numeric CNPJ matches too. This package cannot depend on `@alinea/validation`, so the
 * schema checks the format only; the check digit is verified where the request is handled.
 */
const CNPJ_FORMAT = '[A-Z0-9]{12}\\d{2}';

/**
 * Result of attaching a fiscal document emitted outside the system (uploaded
 * XML) to a DRAFT/BLOCKED invoice — enough to render the attached row.
 */
/** Operator-transcribed facts from an externally issued Brazilian NFS-e PDF. */
export const ArInvoiceFiscalNoteManualSchema = z.object({
  documentType: z.enum(['NFSE', 'NFSE_NACIONAL']),
  accessKey: z.string().trim().min(1).max(50),
  documentNumber: z.string().trim().min(1).max(50),
  issueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  issuerTaxId: z.string().regex(new RegExp(`^${CNPJ_FORMAT}$`)),
  // A CPF is always numeric; only the CNPJ can carry letters.
  recipientTaxId: z.string().regex(new RegExp(`^(\\d{11}|${CNPJ_FORMAT})$`)),
  totalAmount: z.string().regex(/^\d{1,12}(\.\d{1,2})?$/),
  issScenario: z.enum(['ISS_OWED', 'ISS_RETAINED', 'EXEMPT', 'SIMPLES']),
  issAmount: z.string().regex(/^\d{1,12}(\.\d{1,2})?$/),
  irrfAmount: z.string().regex(/^\d{1,12}(\.\d{1,2})?$/),
  pisAmount: z.string().regex(/^\d{1,12}(\.\d{1,2})?$/),
  cofinsAmount: z.string().regex(/^\d{1,12}(\.\d{1,2})?$/),
  csllAmount: z.string().regex(/^\d{1,12}(\.\d{1,2})?$/),
  inssAmount: z.string().regex(/^\d{1,12}(\.\d{1,2})?$/),
});
export type ArInvoiceFiscalNoteManualRequest = z.infer<typeof ArInvoiceFiscalNoteManualSchema>;

export type ArInvoiceFiscalNotePdfExtractWarning =
  | 'REVIEW_REQUIRED'
  | 'MISSING_FIELDS'
  | 'LOW_CONFIDENCE'
  | 'ISSUER_MISMATCH'
  | 'RECIPIENT_MISMATCH'
  | 'WITHHOLDINGS_REQUIRE_REVIEW';

export interface ArInvoiceFiscalNoteCsrfComparison {
  /** Combined PIS/COFINS/CSLL retention printed by the NFS-e. */
  documentAmount: string;
  /** Sum of the invoice's detailed PIS, COFINS and CSLL withholding rows. */
  invoiceAmount: string;
  matches: boolean;
  invoiceBreakdown: {
    pisAmount: string;
    cofinsAmount: string;
    csllAmount: string;
  };
}

/** Unverified PDF suggestions only; the existing inspect/attach workflow still validates them. */
export interface ArInvoiceFiscalNotePdfExtractResponse {
  data: Partial<ArInvoiceFiscalNoteManualRequest>;
  warnings: ArInvoiceFiscalNotePdfExtractWarning[];
  csrfComparison?: ArInvoiceFiscalNoteCsrfComparison;
}

export interface ArInvoiceFiscalNoteUploadResponse {
  fiscalNoteId: string;
  documentType: FiscalNoteDocumentType;
  accessKey: string;
  documentNumber: string | null;
  series: string | null;
  rpsNumber: string | null;
  rpsSeries: string | null;
  /** ISO date (YYYY-MM-DD) the document was issued; the draft now carries it too. */
  issueDate: string;
  totalAmount: number;
  currency: string;
  hasPdf: boolean;
  /** True when the access key already existed (pulled by an integration) and was only linked. */
  reusedExistingNote: boolean;
  /** True when the operator explicitly replaced the draft's monetary values with the document's. */
  appliedFiscalDocumentAmounts: boolean;
  /** PDF metadata is operator-reviewed, never a verified XML signature. */
  verificationSource?: 'VERIFIED_XML' | 'MANUAL_PDF';
}

export interface ArInvoiceFiscalAmountSnapshot {
  totalAmount: number;
  taxTotalAmount: number;
  withholdingTotalAmount: number;
  netAmount: number;
  issAmount: number | null;
  issWithheld: boolean | null;
  withholdings: { taxType: ArWithholdingTaxType; amount: number }[];
}

/**
 * What the upload would attach, before anything is written — the preview the
 * operator confirms. Same guards as the attach; a refusal surfaces here first.
 */
export interface ArInvoiceFiscalNoteInspectResponse {
  verificationSource?: 'VERIFIED_XML' | 'MANUAL_PDF';
  documentType: FiscalNoteDocumentType;
  accessKey: string;
  documentNumber: string | null;
  series: string | null;
  rpsNumber: string | null;
  rpsSeries: string | null;
  /** ISO date (YYYY-MM-DD). */
  issueDate: string;
  totalAmount: number;
  currency: string;
  issuerTaxId: string | null;
  recipientTaxId: string | null;
  /** True when the access key already exists (pulled by an integration) and would only be linked. */
  reusesExistingNote: boolean;
  /** Whether attachment must carry explicit consent to replace the draft's amounts. */
  requiresAmountConfirmation: boolean;
  currentInvoiceAmounts: ArInvoiceFiscalAmountSnapshot;
  fiscalDocumentAmounts: ArInvoiceFiscalAmountSnapshot;
}

/** Stable machine-readable failures returned by fiscal-note upload and approval. */
export const ArFiscalNoteErrorCode = {
  AR_FISCAL_NOTE_PDF_EXTRACTION_FAILED: 'AR_FISCAL_NOTE_PDF_EXTRACTION_FAILED',
  AR_FISCAL_NOTE_PDF_DOCUMENT_UNSUPPORTED: 'AR_FISCAL_NOTE_PDF_DOCUMENT_UNSUPPORTED',
  AR_FISCAL_NOTE_UPLOAD_INVALID_STATUS: 'AR_FISCAL_NOTE_UPLOAD_INVALID_STATUS',
  AR_FISCAL_NOTE_UPLOAD_ALREADY_HAS_NOTE: 'AR_FISCAL_NOTE_UPLOAD_ALREADY_HAS_NOTE',
  AR_FISCAL_NOTE_UPLOAD_COUNTRY_UNSUPPORTED: 'AR_FISCAL_NOTE_UPLOAD_COUNTRY_UNSUPPORTED',
  AR_FISCAL_NOTE_UPLOAD_XML_INVALID: 'AR_FISCAL_NOTE_UPLOAD_XML_INVALID',
  AR_FISCAL_NOTE_UPLOAD_UNSIGNED: 'AR_FISCAL_NOTE_UPLOAD_UNSIGNED',
  AR_FISCAL_NOTE_UPLOAD_ISSUER_MISMATCH: 'AR_FISCAL_NOTE_UPLOAD_ISSUER_MISMATCH',
  AR_FISCAL_NOTE_UPLOAD_RECIPIENT_MISMATCH: 'AR_FISCAL_NOTE_UPLOAD_RECIPIENT_MISMATCH',
  AR_FISCAL_NOTE_UPLOAD_CURRENCY_MISMATCH: 'AR_FISCAL_NOTE_UPLOAD_CURRENCY_MISMATCH',
  AR_FISCAL_NOTE_UPLOAD_TOTAL_MISMATCH: 'AR_FISCAL_NOTE_UPLOAD_TOTAL_MISMATCH',
  AR_FISCAL_NOTE_UPLOAD_AMOUNT_CONFIRMATION_REQUIRED:
    'AR_FISCAL_NOTE_UPLOAD_AMOUNT_CONFIRMATION_REQUIRED',
  AR_FISCAL_NOTE_UPLOAD_ISS_MISMATCH: 'AR_FISCAL_NOTE_UPLOAD_ISS_MISMATCH',
  AR_FISCAL_NOTE_UPLOAD_TAX_MISMATCH: 'AR_FISCAL_NOTE_UPLOAD_TAX_MISMATCH',
  AR_FISCAL_NOTE_UPLOAD_FISCAL_PARAMETERS_MISSING:
    'AR_FISCAL_NOTE_UPLOAD_FISCAL_PARAMETERS_MISSING',
  AR_FISCAL_NOTE_UPLOAD_ALREADY_LINKED: 'AR_FISCAL_NOTE_UPLOAD_ALREADY_LINKED',
  AR_FISCAL_NOTE_UPLOAD_NOTE_CANCELLED: 'AR_FISCAL_NOTE_UPLOAD_NOTE_CANCELLED',
  AR_FISCAL_NOTE_UPLOAD_EMISSION_IN_PROGRESS: 'AR_FISCAL_NOTE_UPLOAD_EMISSION_IN_PROGRESS',
  AR_FISCAL_NOTE_UPLOAD_CONCURRENT_CHANGE: 'AR_FISCAL_NOTE_UPLOAD_CONCURRENT_CHANGE',
  AR_FISCAL_NOTE_UPLOAD_CFDI_NOT_INGRESO: 'AR_FISCAL_NOTE_UPLOAD_CFDI_NOT_INGRESO',
  AR_FISCAL_NOTE_UPLOAD_FILE_REQUIRED: 'AR_FISCAL_NOTE_UPLOAD_FILE_REQUIRED',
  AR_FISCAL_NOTE_UPLOAD_MANUAL_INVALID: 'AR_FISCAL_NOTE_UPLOAD_MANUAL_INVALID',
  AR_FISCAL_NOTE_UPLOAD_MANUAL_REVIEW_REQUIRED: 'AR_FISCAL_NOTE_UPLOAD_MANUAL_REVIEW_REQUIRED',
  AR_FISCAL_NOTE_UPLOAD_MANUAL_EXISTING_NOTE: 'AR_FISCAL_NOTE_UPLOAD_MANUAL_EXISTING_NOTE',
  AR_INVOICE_ISSUE_FISCAL_NOTE_MISMATCH: 'AR_INVOICE_ISSUE_FISCAL_NOTE_MISMATCH',
} as const;

export type ArFiscalNoteErrorCode =
  (typeof ArFiscalNoteErrorCode)[keyof typeof ArFiscalNoteErrorCode];

export const AR_FISCAL_NOTE_ERROR_CODES = Object.values(ArFiscalNoteErrorCode);

/** Structured details used by the Web client to render actionable upload errors. */
export interface ArFiscalNoteErrorResponse {
  code?: string;
  message?: string;
  /** Machine-readable upload failure reason; unknown values must use a safe fallback. */
  reason?: string;
  documentTotal?: string | null;
  invoiceTotal?: string | null;
  documentCurrency?: string | null;
  invoiceCurrency?: string | null;
  documentIss?: string | null;
  invoiceIss?: string | null;
  issuerTaxId?: string | null;
  recipientTaxId?: string | null;
  emissionStatus?: string | null;
}

/** Linked fiscal note summary on AR invoice detail. */
export interface ArInvoiceDetailFiscalNote {
  verificationSource?: 'VERIFIED_XML' | 'MANUAL_PDF' | null;
  /** True when this note was attached through the manual fiscal-document upload flow. */
  isUploaded?: boolean;
  /** Server-derived deletion eligibility; false for shared or provider-owned notes. */
  canClearUploaded?: boolean;
  fiscalNoteId: string;
  accessKey: string;
  documentNumber?: string | null;
  series?: string | null;
  rpsNumber?: string | null;
  rpsSeries?: string | null;
  documentType: FiscalNoteDocumentType;
  nfseIssueDate?: string | null;
  hasPdf: boolean;
  /**
   * Receivables this document bills. A consolidated document covers a whole
   * period for one customer, so anything above one means this invoice is one of
   * many on it — worth saying, or the reader assumes the document is its own.
   */
  invoiceCount?: number;
  /** Provider status of the note ("cancelado" once cancelled). */
  status?: string | null;
  /** Set when the note was cancelled; a cancelled note no longer covers the invoice. */
  cancelledAt?: string | null;
}

/** Withholding row on AR invoice detail. */
export interface ArInvoiceDetailWithholding {
  withholdingId: string;
  taxType: ArWithholdingTaxType;
  amount: number;
}

/** Enriched classification row on AR invoice detail (mirrors AP bill enrichment). */
export interface ArClassification {
  classificationId: string;
  invoiceId: string;
  lineId?: string | null;
  allocationType: ApAllocationType;
  glAccounts: DimensionSplit[];
  departments?: DimensionSplit[];
  costCenters?: DimensionSplit[];
  managerialAccounts?: DimensionSplit[];
  segments?: SegmentSplit[];
  customBreakdown?: CustomBreakdown;
  notes?: string | null;
  source?: ArClassificationSource;
}

/** Payload for saving AR invoice classifications (draft / pre-GL). */
export interface SaveArClassificationsPayload {
  classifications: {
    lineId?: string;
    allocationType?: ApAllocationType;
    glAccounts?: DimensionSplitInput[];
    departments?: DimensionSplitInput[];
    costCenters?: DimensionSplitInput[];
    managerialAccounts?: DimensionSplitInput[];
    segments?: SegmentSplitInput[];
    customBreakdown?: CustomBreakdownInput;
    notes?: string;
  }[];
}

/** Invoice line on AR invoice detail. */
export interface ArInvoiceDetailLine {
  lineId: string;
  lineNumber: number;
  description?: string | null;
  quantity?: number | null;
  unitPrice?: number | null;
  lineAmount?: number | null;
  productCode?: string | null;
  productName?: string | null;
  productType?: string | null;
  productExternalId?: string | null;
  /**
   * Who issues this line's NF (ALC-5094): the value frozen at approval, else the service's
   * own setting; null when the line has no service.
   */
  nfIssuer?: BillingServiceNfIssuer | null;
}

export interface ArNfseEmissionErrorItem {
  field?: string;
  message: string;
  code?: string;
  activePostingCount?: number;
  requestedPostingCount?: number;
  activePostingId?: string;
}

export interface ArNfseEmissionSummaryResponse {
  emissionId: string;
  environment: 'homologacao' | 'producao' | null;
  status: ArNfseEmissionStatus;
  emissionMode: ArNfseEmissionMode;
  reference: string;
  errors: ArNfseEmissionErrorItem[];
  fiscalNoteId: string | null;
  accessKey: string | null;
  hasPdf: boolean;
  submittedAt: string | null;
  authorizedAt: string | null;
  lastCheckedAt: string | null;
  attempts: number;
  cancellation?: ArNfseCancellationSummaryResponse | null;
}

export interface ArNfseCancellationSummaryResponse {
  cancellationId: string;
  status: ArNfseCancellationStatus;
  cancelType: ArNfseCancelType;
  postCancelAction: ArNfseCancelAction;
  justificativa: string;
  reversalDate: string;
  requestedAt: string;
  confirmedAt: string | null;
  errors: ArNfseEmissionErrorItem[];
}

/**
 * Which journal an AR invoice posting tracks. An invoice's accrual and its post-then-emit ISS
 * reclassification are two active postings that can share a book (ALC-1332), so the reversal
 * flow addresses postings, not books.
 */
export type ArInvoicePostingKind = 'ACCRUAL' | 'ISS_RECLASS';

/**
 * D46-3 (accounting review W-3): one foreign settlement's FOREIGN_ADVANCE customer credit and its
 * release to revenue. `release` is the live release (null while the credit is open).
 */
export interface ArCustomerCreditReleaseItem {
  /** The settlement recognition journal that credited the customer advance. */
  sourceJournalId: string;
  code: string;
  documentCurrency: string;
  /** Document-currency units held as a customer credit. */
  amount: string;
  /** Functional amount credited (what a release debits — historical rate, no FX). */
  functionalAmount: string | null;
  exchangeRate: string | null;
  advanceGlAccountId: string;
  lines: {
    lineId: string;
    serviceStart: string | null;
    serviceEnd: string | null;
    amount: string;
  }[];
  release: {
    releaseJournalId: string;
    competencyMonth: string;
    periodId: string;
    journalDate: string;
    functionalAmount: string;
    releasedAt: string;
    releasedBy: string | null;
  } | null;
}

export interface ArCustomerCreditReleaseState {
  invoiceId: string;
  functionalCurrency: string | null;
  /** Credits still held on the customer advance (the alert shows while > 0). */
  openCount: number;
  items: ArCustomerCreditReleaseItem[];
}

export interface ArCustomerCreditReleaseRequest {
  /** `YYYY-MM`: the competency month the revenue is recognized in (its period must be OPEN). */
  competencyMonth: string;
}

export interface ArCustomerCreditReleaseReverseRequest {
  /** The release to reverse; omitted → every live release of the invoice. */
  releaseJournalId?: string;
  /** `YYYY-MM-DD`; default: the release's own journal date. Its period must be OPEN. */
  reversalDate?: string;
}

/**
 * D47: one cost provision the release booked for its revenue (Cost Services, e.g. Royalty 40 %).
 * PENDING = written but not in the GL yet (a setup gap: account, vendor, rate) — "Lançar pendentes"
 * posts it once fixed.
 */
export interface ArCustomerCreditReleaseCostProvision {
  releaseJournalId: string;
  entryId: string;
  costServiceId: string;
  /** The cost journal (Dr cost / Cr provision) of this call, null while PENDING. */
  costJournalId: string | null;
  postingState: 'PENDING' | 'POSTED';
  pendingReason: string | null;
  /** Vendor currency units and the functional amount, exact decimal strings. */
  amount: string;
  currency: string;
  functionalAmount: string;
}

export interface ArCustomerCreditReleaseResult {
  /** True when this call posted a journal; false on an idempotent repeat or a reversal. */
  posted: boolean;
  /** Journals this call wrote (release journals, or reversal journals for a reverse). */
  journalIds: string[];
  /** D47: the cost provisions a release booked ([] on a repeat or a reverse). */
  costProvisions: ArCustomerCreditReleaseCostProvision[];
  /** D47: the cost mirror journals a reverse posted ([] on a release). */
  costReversalJournalIds: string[];
  state: ArCustomerCreditReleaseState;
}

export interface ArNfseCancelReversalLine {
  originalJournalLineId?: string | null;
  glAccountId: string;
  description?: string | null;
  debitAmount: number;
  creditAmount: number;
  bookId: string;
  /** The posting this line reverses. Required on input — `bookId` alone is ambiguous. */
  postingId?: string;
  /** Explicitly marks a manual balancing/adjustment line that was not part of the original posting. */
  isAdjustment?: boolean;
  costCenterId?: string | null;
  departmentId?: string | null;
  managerialAccountId?: string | null;
  segmentJson?: string | null;
  /** When true, the UI should default this original line to "keep" (not reversed). */
  defaultKeep?: boolean;
  /** Tax slot hint for ISS provisioning legs (DEDUCAO_ISS, ISS_PROPRIO). */
  taxSlotCode?: string | null;
}

export interface ArNfseCancelReversalOriginalLine {
  journalLineId: string;
  glAccountId: string;
  accountCode: string;
  accountName: string;
  description: string | null;
  debitAmount: number;
  creditAmount: number;
  taxSlotCode?: string | null;
  defaultKeep?: boolean;
}

/**
 * One reversible posting. Named `Book…` for backwards compatibility, but keyed by `postingId`:
 * an invoice whose ISS provision was reclassified after posting has TWO entries for the same
 * book (ALC-1332) — its accrual and the reclassification — and they reverse independently.
 */
export interface ArNfseCancelReversalBookPreview {
  postingId: string;
  postingKind: ArInvoicePostingKind;
  bookId: string;
  bookCode: string;
  bookName: string;
  originalLines: ArNfseCancelReversalOriginalLine[];
  proposedReversalLines: ArNfseCancelReversalLine[];
  totalDebit: number;
  totalCredit: number;
  /**
   * An entry whose every original line defaults to "keep" proposes NO reversal lines — an
   * administrative cancellation of a reclassification, where the tax already remitted stands.
   * That is a balanced no-op, not an unbalanced entry.
   */
  isBalanced: boolean;
}

export interface ArNfseCancelReversalPreviewResponse {
  invoiceId: string;
  reversalDate: string;
  minReversalDate: string;
  books: ArNfseCancelReversalBookPreview[];
  /** `code` is set on a stable refusal, such as `AR_RECEIVE_FIRST_NF_CANCEL_ISS_NOT_RECLASSIFIED`. */
  validationErrors?: { field: string; message: string; code?: string }[];
}

export interface ArNfseCancelReversalBookRequest {
  /**
   * The posting being reversed. Required: an invoice can have two active postings in one book,
   * so `bookId` does not identify a journal (ALC-1332).
   */
  postingId: string;
  bookId: string;
  /** Empty means "keep every line" — the posting survives the cancellation untouched. */
  lines: ArNfseCancelReversalLine[];
}

export interface RequestArNfseCancelRequest {
  cancelType: ArNfseCancelType;
  postCancelAction: ArNfseCancelAction;
  justificativa: string;
  reversalDate?: string;
  books: ArNfseCancelReversalBookRequest[];
}

export interface ConfirmArNfseCancelRequest {
  justificativa?: string;
  reversalDate?: string;
  books?: ArNfseCancelReversalBookRequest[];
}

export interface ArNfseCancelSurvivingLeg {
  glAccountId: string;
  accountCode: string;
  accountName: string;
  amount: number;
  side: 'DEBIT' | 'CREDIT';
  reason: string;
  fiscalNoteId?: string | null;
  originalJournalLineId?: string | null;
  /** Which posting the surviving leg belongs to (ALC-1332). */
  postingId?: string;
}

export type ArNfseCancelRequestResponse =
  | { outcome: 'ENQUEUED'; cancellationId: string }
  | { outcome: 'PENDING_ADMINISTRATIVE'; cancellationId: string }
  | { outcome: 'CONFIRMED'; cancellationId: string; reversalJournalIds: string[] }
  | {
      outcome: 'VALIDATION_ERROR';
      /** `code` is set for a coded refusal (AR_NFSE_CANCEL_REVERSAL_*), for the UI to translate. */
      errors: { field: string; message: string; code?: string }[];
    };

export type ArNfseEmitTriggerResponse =
  | { outcome: 'ENQUEUED'; emissionId: string }
  | { outcome: 'FOCUSNFE_NOT_CONFIGURED' }
  | { outcome: 'SKIPPED'; reason: string }
  | { outcome: 'VALIDATION_ERROR'; emissionId: string; errors: ArNfseEmissionErrorItem[] };

/** GET /ar/invoices/:invoiceId response shape. */
export type ArInvoiceDocumentType = 'STANDARD' | 'TRANSACTION' | 'GTV_TRANSACTION' | 'AGGREGATE';

/** Who publishes the exchange rate a book converts at. */
export type ArFxRateAuthority = 'BANXICO_DOF' | 'BCB_PTAX' | 'FRB_H10';

/**
 * What a settlement AGGREGATE shows about the per-reservation transactions it bills.
 * Revenue and IVA are recognised on each child (ON_SETTLEMENT), never on the aggregate.
 */
export interface ArSettlementAggregateSummary {
  /** Transactions the aggregate links. */
  childCount: number;
  /** Of those, how many carry a live recognition journal. */
  recognizedInvoiceCount: number;
  /** The book's functional currency; null when it could not be resolved. */
  functionalCurrency: string | null;
  /** Sum of the children's recognised revenue, in the functional currency. */
  recognizedRevenueAmount: number;
  /** Sum of the children's recognised IVA/ISS, in the functional currency. */
  recognizedOwnTaxAmount: number;
  /** The commission was collected by retention at settlement: no child had an open balance to bill. */
  settledByRetention: boolean;
}

/**
 * What approving an aggregate persists in `metadata.glReclassification.reason` when no
 * child carried an open balance to bill. Exported so SQL that has to recognise the same
 * decision (the list tiles) binds this value rather than re-typing the sentence.
 */
export const NO_OPEN_CHILD_BALANCE_REASON = 'no child carries an open unbilled balance';
/** `metadata.glReclassification.status` of an approval that posted nothing. */
export const RECLASSIFICATION_NOTHING_TO_POST_STATUS = 'NOTHING_TO_POST';

/**
 * True when approving the aggregate found no child with an open balance, so the
 * commission was retained when the deposit was netted and the aggregate has nothing
 * left to collect. Read off the decision the approval persisted on the aggregate
 * (`metadata.glReclassification`), which is the books' own answer.
 */
export function isSettlementAggregateSettledByRetention(metadata: unknown): boolean {
  const reclass = (
    metadata as { glReclassification?: { status?: unknown; reason?: unknown } } | null
  )?.glReclassification;
  return (
    reclass?.status === RECLASSIFICATION_NOTHING_TO_POST_STATUS &&
    reclass.reason === NO_OPEN_CHILD_BALANCE_REASON
  );
}

/** One boleto of an invoice, as the invoice detail returns it. */
export interface ArInvoiceBoletoResponse {
  boletoId: string;
  scheduleId: string | null;
  installmentNumber: number | null;
  status: ArBoletoStatus;
  nossoNumero: string;
  linhaDigitavel: string | null;
  barcode: string | null;
  /** `YYYY-MM-DD`. */
  dueDate: string;
  /** Decimal string, in `currencyCode`. */
  amount: string;
  /** The boleto's own currency, as stored. */
  currencyCode: string;
  paidAmount: string | null;
  paidAt: string | null;
  lastError: string | null;
  registeredAt: string | null;
  cancelledAt: string | null;
}

/** Why the boletos of an invoice are missing, when issuing them failed and was not retried since. */
export interface ArInvoiceBoletoIssueError {
  code: string | null;
  message: string;
  occurredAt: string;
  /** The bank was unreachable: the worker retries the issue on its own. */
  willRetry?: boolean;
}

export interface ArInvoiceBoletoRetryResponse {
  enqueued: boolean;
  action: 'ISSUE' | 'CANCEL';
}

export interface ArInvoiceDetailResponse {
  invoiceId: string;
  /** Revenue cycle frozen on the invoice at creation (ALC-5089). Later edits never move it. */
  revenueCycle?: ResolvedRevenueCycle;
  /** IANA zone of the entity's book calendar: the zone a history timestamp's day is read in. */
  bookTimeZone?: string | null;
  entityId: string;
  customerId: string;
  /** Document kind: STANDARD emits its own fiscal document; TRANSACTION / GTV_TRANSACTION
   *  are billable events gathered by pre-billing into an AGGREGATE, which is the document. */
  invoiceType?: ArInvoiceDocumentType;
  /** Gross transaction value behind a GTV_TRANSACTION (sum over members on an AGGREGATE). */
  gtvAmount?: number | null;
  invoiceNumber?: string | null;
  externalRef?: string | null;
  issueDate?: string | null;
  dueDate?: string | null;
  competencyDate?: string | null;
  managerialDate?: string | null;
  currencyCode?: string | null;
  invoiceStatus: ArInvoiceStatus;
  description?: string | null;
  createdAt?: string | null;
  subtotalAmount?: number | null;
  taxTotalAmount?: number | null;
  totalAmount?: number | null;
  withholdingTotalAmount?: number | null;
  netAmount?: number | null;
  /**
   * Commercial face and already-posted CPC 47 basis when the invoice denomination differs
   * from the entity's functional currency. Recognition amounts and rate are immutable GL
   * evidence; they are not a preview of the emission-day fiscal conversion.
   */
  foreignCurrencyRecognition?: {
    transactionCurrency: string;
    originalAmount: number;
    functionalCurrency: string;
    recognizedRevenueAmount: number;
    recognizedOwnTaxAmount: number;
    recognitionExchangeRate: number | null;
    recognitionExchangeRateSource: string | null;
    recognitionExchangeRateDate: string | null;
    /** Functional-currency provision fixed by the authorized fiscal emission. */
    issuanceProvision: {
      functionalAmount: number;
      ownTaxAmount: number;
      exchangeRate: number;
      exchangeRateSource: string | null;
      exchangeRateDate: string | null;
    } | null;
  } | null;
  /** @deprecated Use classifications; removed after Phase 2 cutover. */
  revenueGlAccountId?: string | null;
  /** @deprecated Use classifications; removed after Phase 2 cutover. */
  revenueGlAccountName?: string | null;
  classificationMode?: ApClassificationMode | null;
  classifications?: ArClassification[];
  hasFiscalNote: boolean;
  hasGlPosting: boolean;
  /**
   * The invoice's services issue their NF outside Alchemy ERP and none is linked yet
   * (ALC-5091). On an invoice that also has in-Alchemy lines it is information only (D26).
   */
  externalFiscalNotePending?: boolean;
  /** The NF issued outside Alchemy ERP, once linked: number, issue date (ISO), ISS amount. */
  externalFiscalNoteNumber?: string | null;
  externalFiscalNoteIssueDate?: string | null;
  externalFiscalNoteIssAmount?: string | number | null;
  /** Revenue lifecycle: settlement-sourced invoices are recognized at import
   *  (ON_SETTLEMENT); everything else is doc-first (ON_FISCAL_DOCUMENT). */
  revenueRecognitionModel: 'ON_FISCAL_DOCUMENT' | 'ON_SETTLEMENT';
  /** Settlement economics, present only for settlement-sourced invoices. On a
   *  CUSTODIAL contract the repasse (linked events' net_deposit) passes through
   *  as a liability and GTV = fee + tax + repasse is what lands in transit. */
  settlement?: {
    model: 'DIRECT' | 'CUSTODIAL' | null;
    partnerShareAmount: number;
    gtvAmount: number;
  } | null;
  /** ISO country of the issuing entity — drives the fiscal framework shown
   *  (BR: ISS/NFS-e/IBS; MX: IVA/CFDI). */
  fiscalCountry?: string | null;
  /**
   * The authority that prices this invoice's foreign-currency conversions, chosen by the
   * entity book's framework (MX_NIF -> the DOF FIX published by Banxico; otherwise the
   * BCB PTAX). Null when the invoice is in the functional currency or no book is known.
   */
  fxRateAuthority?: ArFxRateAuthority | null;
  /**
   * AGGREGATE of settlement-sourced transactions only: what its children recognised and
   * whether the commission was already retained at settlement. Null on every other invoice.
   */
  settlementAggregate?: ArSettlementAggregateSummary | null;
  nfseEmission?: ArNfseEmissionSummaryResponse | null;
  /** Boletos issued for this invoice (ALC-4804). */
  boletos?: ArInvoiceBoletoResponse[];
  /** The boleto issue failure still standing on this invoice, if any. */
  boletoIssueError?: ArInvoiceBoletoIssueError | null;
  /** ISSUED, paid by boleto and with no live boleto: the user may send the billing ("Enviar cobrança"). */
  canSendBoletoBilling?: boolean;
  /**
   * When `invoiceStatus === 'BLOCKED'`, the latest blocking reason (raw, English) derived
   * from invoice history so the UI can explain why the invoice is stuck and how to recover.
   */
  blockedReason?: string | null;
  /** Machine-readable error code for the block (e.g. `TAX_ACCOUNT_NOT_MAPPED`), when known. */
  blockedErrorCode?: string | null;
  /** Pipeline stage that triggered the block (e.g. `GL_POSTING`, `NFSE_EMISSION`). */
  blockedStage?: string | null;
  /** First GL book with an invoice posting, used for reversal period selection. */
  postingBookId?: string | null;
  fiscalNotes: ArInvoiceDetailFiscalNote[];
  withholdings: ArInvoiceDetailWithholding[];
  lines: ArInvoiceDetailLine[];
  customer?: {
    customerId: string;
    code?: string | null;
    party?: {
      legalName?: string | null;
      tradeName?: string | null;
      taxIdentifiers?: { value: string }[];
    } | null;
  } | null;
  contracts?: { contractId: string; code: string }[];
  /**
   * True when this invoice was generated from a billing contract AND every one of its
   * lines carries a system-derived revenue classification, taken from the contract item's
   * frozen pricing snapshot. The classification and line-item editors are read-only in
   * that case: the contract is the source of truth, and hand-editing would diverge the
   * invoice's accounting from it.
   *
   * False when any line is missing that derivation (e.g. a service frozen before it had a
   * classification template) — such an invoice cannot post as-is, so it stays editable
   * rather than becoming unfixable.
   */
  contractClassificationLocked?: boolean;
  entity?: { entityId: string; name?: string | null } | null;
  allocations?: {
    amount?: number | null;
    allocationDate?: string | null;
    receipt?: {
      receiptId: string;
      receiptNumber?: string | null;
      receiptDate?: string | null;
      amount?: number | null;
      paymentMethod?: string | null;
      externalRef?: string | null;
    } | null;
  }[];
  /** Payment schedules (installments), open ones annotated with expected cash (ALC-1418). */
  schedules?: ArInvoiceScheduleResponse[];
  history?: ArInvoiceHistoryEntry[];
  /** The invoice's open issues, in display order (ALC-5185). */
  openIssues?: ArInvoiceListedIssue[];
  /**
   * Pass-through integrator metadata (ALC-2236) — flat string key/value pairs attached by
   * an External API caller (e.g. coupon, sales channel, offer type). v1 is display-only:
   * no key registry, no schema, no in-app editor.
   */
  metadata?: Record<string, string> | null;
}

/** One AR payment schedule (installment) row as returned inside invoice responses. */
export interface ArInvoiceScheduleResponse {
  scheduleId: string;
  invoiceId: string;
  entityId: string;
  installmentNumber: number;
  dueDate?: string | null;
  amount?: number | null;
  paid?: number | null;
  status?: string | null;
  /** Present only on OPEN schedules of not-yet-settled invoices (ALC-1418). */
  expectedCash?: ArScheduleExpectedCash | null;
}

export interface ArInvoiceHistoryEntry {
  historyId: string;
  eventType: string;
  oldStatus?: ArInvoiceStatus | null;
  newStatus?: ArInvoiceStatus | null;
  payload?: Record<string, unknown> | null;
  createdAt: string;
  createdBy?: string | null;
  /** Who wrote the row: the user's name; null when the user is unknown (ALC-5185). */
  createdByName?: string | null;
  /** The row was written by a system user (an integration or a job), not a person. */
  createdBySystem?: boolean;
}

export interface CustomerAddressRequest {
  addressType?: AddressType;
  line1: string;
  number?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  countryCode?: string | null;
  isPrimary?: boolean;
}

export interface CreateArCustomerRequest {
  entityId: string;
  /** When omitted or empty, a code is auto-generated (e.g. CLI-001). */
  code?: string | null;
  partyType?: PartyType;
  /** ISO 3166-1 alpha-2 country of residence or incorporation. */
  countryCode?: string | null;
  legalName: string;
  tradeName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  taxIdType?: TaxIdType;
  taxIdValue?: string | null;
  creditLimit?: string | null;
  paymentTerms?: string | null;
  /** SAT c_RegimenFiscal of the customer as a CFDI recipient (Mexico). */
  taxRegimeCode?: string | null;
  /** SAT c_FormaPago ("Tipo de Pago") the customer's CFDI states (Mexico). */
  paymentFormCode?: string | null;
  isBlocked?: boolean;
  blockReason?: string | null;
  billingGroupId?: string | null;
  boletoInstructions?: string | null;
  negativacaoEnabled?: boolean | null;
  negativacaoDays?: number | null;
  autoCollectionEnabled?: boolean | null;
  communicationChannels?: string[];
  hubspotContactId?: string | null;
  intercomContactId?: string | null;
  billingNotes?: string | null;
  glOffsetAccountId?: string | null;
  address?: CustomerAddressRequest;
  serproValidated?: boolean;
  /** Reference to the stored SERPRO response from the lookup call. Prevents a second SERPRO request on create. */
  serproDataId?: string | null;
}

/**
 * What a changed customer document means (ALC-4673, D10). Never guessed: a caller that changes the
 * document says which one, and the update is refused (400 `CUSTOMER_TAX_ID_CHANGE_CHOICE_REQUIRED`)
 * when it does not.
 *
 * - `CORRECT`: the same company or person, the document was wrong. It is changed on the customer's
 *   current record, which keeps everything else (history, addresses). The wrong document is deleted
 *   (its real owner can then be created; the audit log keeps it), and a vendor record sharing the
 *   customer's record gets the corrected document too.
 * - `NEW_PARTY`: another company or person. The customer moves to a record of its own, with the
 *   addresses it had; the old record keeps its history.
 */
export type ArCustomerTaxIdChange = 'CORRECT' | 'NEW_PARTY';

export interface UpdateArCustomerRequest extends Partial<CreateArCustomerRequest> {
  /** Required when `taxIdType` / `taxIdValue` change the customer's document; ignored otherwise. */
  taxIdChange?: ArCustomerTaxIdChange;
}

export interface ArCustomerResponse {
  customerId: string;
  entityId: string;
  partyId: string;
  /**
   * The customer's record (party) is also used elsewhere: it is a vendor or an employee, or a
   * customer of another entity. Correcting its document changes that record too. Only the single
   * customer read returns it.
   */
  partyShared?: boolean;
  code?: string | null;
  creditLimit?: string | null;
  paymentTerms?: string | null;
  /** SAT c_RegimenFiscal of the customer as a CFDI recipient (Mexico). */
  taxRegimeCode?: string | null;
  /** SAT c_FormaPago ("Tipo de Pago") the customer's CFDI states (Mexico). */
  paymentFormCode?: string | null;
  isBlocked: boolean;
  blockReason?: string | null;
  billingGroupId?: string | null;
  boletoInstructions?: string | null;
  negativacaoEnabled?: boolean | null;
  negativacaoDays?: number | null;
  autoCollectionEnabled?: boolean | null;
  communicationChannels: string[];
  hubspotContactId?: string | null;
  intercomContactId?: string | null;
  billingNotes?: string | null;
  glOffsetAccountId?: string | null;
  party?: PartyResponse | null;
  serproFiscal?: CustomerSerproFiscalInfo | null;
  /** ISO date when SERPRO data was first stored (verification date). */
  serproVerifiedAt?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface CustomerSerproFiscalInfo {
  regimeTributario?: string | null;
  porte?: string | null;
  situacaoCadastral?: string | null;
  naturezaJuridica?: string | null;
  cnaePrincipal?: string | null;
  dataAbertura?: string | null;
}

export interface CustomerSerproLookupResponse {
  legalName: string;
  tradeName?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: CustomerAddressRequest;
  fiscal?: CustomerSerproFiscalInfo | null;
  /** ID of the stored SERPRO response – pass back as serproDataId on create to avoid a second API call. */
  serproDataId?: string;
}

export interface CustomerDetailResponse {
  customer: ArCustomerResponse;
  billingGroup?: BillingGroupResponse | null;
  emails: CustomerEmailResponse[];
  balance?: CustomerBalanceSummaryResponse | null;
}

/** Maximum invoices accepted by a synchronous tax-recalculation batch request. */
export const MAX_AR_INVOICE_TAX_RECALCULATION_BATCH_COUNT = 50;

export interface BatchRecalculateArInvoiceTaxesRequest {
  invoiceIds: string[];
}

export interface BatchRecalculateArInvoiceTaxesResult {
  invoiceId: string;
  success: boolean;
  error?: string;
}

export interface BatchRecalculateArInvoiceTaxesResponse {
  total: number;
  succeeded: number;
  failed: number;
  results: BatchRecalculateArInvoiceTaxesResult[];
}

/** Maximum invoices accepted by a synchronous uploaded-fiscal-note clear request. */
export const MAX_AR_INVOICE_FISCAL_NOTE_CLEAR_BATCH_COUNT = 50;

export interface ClearUploadedArInvoiceFiscalNoteResponse {
  invoiceId: string;
  clearedFiscalNoteIds: string[];
  clearedDocumentCount: number;
}

export interface BatchClearUploadedArInvoiceFiscalNotesRequest {
  invoiceIds: string[];
}

export interface BatchClearUploadedArInvoiceFiscalNotesResult {
  invoiceId: string;
  success: boolean;
  error?: string;
}

export interface BatchClearUploadedArInvoiceFiscalNotesResponse {
  total: number;
  succeeded: number;
  failed: number;
  results: BatchClearUploadedArInvoiceFiscalNotesResult[];
}

/** The Clientes listing's status: a customer is active or blocked (`isBlocked`). */
export const AR_CUSTOMER_LISTING_STATUSES = ['ACTIVE', 'BLOCKED'] as const;
export type ArCustomerListingStatus = (typeof AR_CUSTOMER_LISTING_STATUSES)[number];

/** The Clientes listing's sortable columns; `name` is the party's legal name. */
export const AR_CUSTOMER_SORT_COLUMNS = ['name', 'code', 'email', 'document', 'status'] as const;
export type ArCustomerSortColumn = (typeof AR_CUSTOMER_SORT_COLUMNS)[number];

/** `GET /ar/customers/listing` and `/ar/customers/status-counts` query. */
export interface FindArCustomersQuery {
  /** Several statuses (the status column filter or a status view). */
  statusIn?: ArCustomerListingStatus[];
  /** Restrict to these customers (the Cliente column's picks); still tenant-scoped. */
  customerIds?: string[];
  /** Code contains this, case-insensitive. */
  code?: string;
  /** E-mail contains this, case-insensitive. */
  email?: string;
  /** Any CNPJ/CPF of the customer contains this, punctuation ignored. */
  document?: string;
  /** Name, trade name, code, e-mail or document contains this (the Cliente filter's search). */
  search?: string;
  sortBy?: ArCustomerSortColumn;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

/** One row of the Clientes listing. */
export interface ArCustomerListItem {
  customerId: string;
  code: string | null;
  status: ArCustomerListingStatus;
  legalName: string;
  tradeName: string | null;
  email: string | null;
  /** The primary tax identifier (CNPJ/CPF), as stored. */
  taxId: string | null;
  entityId: string;
  entityName: string | null;
}

export interface ArCustomerListingResponse {
  items: ArCustomerListItem[];
  total: number;
  page: number;
  limit: number;
}

/** Customers per status over every filter but the status, plus `all`. */
export type ArCustomerStatusCounts = Record<ArCustomerListingStatus | 'all', number>;

/** Body of `POST /ar/invoices/:invoiceId/external-fiscal-note` (ALC-5091). */
export interface ArInvoiceExternalFiscalNoteLinkRequest {
  /** Number of the NF the customer's own system issued. */
  number: string;
  /** `YYYY-MM-DD`: the NF's issue date, which dates the ISS reclassification. */
  issueDate: string;
  /** The ISS the NF states, as a decimal string. `0` means the NF owes none. */
  issAmount: string;
}

export interface ArInvoiceExternalFiscalNoteLinkResponse {
  invoiceId: string;
  externalFiscalNoteNumber: string;
  externalFiscalNoteIssueDate: string;
  /** Null when an NF linked through import or the External API stated no ISS. */
  externalFiscalNoteIssAmount: string | null;
  externalFiscalNotePending: boolean;
  /** False when the same NF was already linked: nothing changed. */
  linked: boolean;
  /** The ISS reclassification journals, empty when no ISS was waiting on the NF. */
  reclassJournalIds: string[];
}
